import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { customerService } from '@/services/customer.service';
import {
  equipmentIntakeService,
  type AddressSelection,
  type EquipmentIntake,
} from '@/services/equipment-intake.service';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { AddressSection } from './AddressSection';
import { toast } from '@/lib/swal';

/**
 * Manual Bill-To / Ship-To change on a saved intake.
 *
 * This is the ONLY UI path that mutates a frozen snapshot, and it exists so the
 * change is always a deliberate human act. Nothing here may be triggered by a
 * customer edit, a branch edit, a GST change or a Received-From correction —
 * automatic synchronization is forbidden.
 *
 * Sends SELECTIONS only. The server re-resolves against current master data and
 * writes a new frozen snapshot, emitting an audit event that records the
 * decision (source + locationId) and never the resolved address.
 */
export function EditAddressesModal({
  open,
  intake,
  onClose,
  onSaved,
}: {
  open: boolean;
  intake: EquipmentIntake;
  onClose: () => void;
  onSaved: (updated: EquipmentIntake) => void;
}) {
  const queryClient = useQueryClient();

  // Seeded from the stored provenance ids, not from the snapshot text — the
  // snapshot is a historical record, the provenance is what was selected.
  const [billTo, setBillTo] = useState<AddressSelection>(
    intake.billToLocationId
      ? { source: 'LOCATION', locationId: intake.billToLocationId }
      : { source: 'CUSTOMER_DEFAULT' },
  );
  const [shipTo, setShipTo] = useState<AddressSelection>(
    intake.shipToLocationId
      ? { source: 'LOCATION', locationId: intake.shipToLocationId }
      : { source: 'CUSTOMER_DEFAULT' },
  );

  const { data: locations } = useQuery({
    queryKey: ['customer-locations', intake.customerId],
    queryFn: () => customerService.listLocations(intake.customerId as string),
    enabled: open && !!intake.customerId,
  });

  // The intake carries only the customer's name; the preview needs the default
  // address and the GSTIN, so the full record is resolved by id.
  const { data: customer } = useQuery({
    queryKey: ['customer-resolve', intake.customerId],
    queryFn: () => customerService.get(intake.customerId as string),
    enabled: open && !!intake.customerId,
  });

  const activeLocations = (locations ?? []).filter((l) => !l.deletedAt);

  const mutation = useMutation({
    mutationFn: () => equipmentIntakeService.updateAddresses(intake.id, { billTo, shipTo }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['equipment-intakes'] });
      toast.success('Bill-To / Ship-To updated');
      onSaved(updated);
      onClose();
    },
    onError: (error: Error) => {
      let message = 'Could not update addresses';
      if (axios.isAxiosError(error)) {
        const body = error.response?.data as { error?: { message?: string } } | undefined;
        if (body?.error?.message) message = body.error.message;
      }
      toast.error(message);
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Change Bill-To / Ship-To — ${intake.intakeNumber}`}
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Changing these does not alter any document already issued. Quotations, invoices and
          dispatch notes keep the addresses they were issued with.
        </p>

        <AddressSection
          customerId={intake.customerId as string}
          customer={customer ?? null}
          locations={activeLocations}
          billTo={billTo}
          shipTo={shipTo}
          onBillToChange={setBillTo}
          onShipToChange={setShipTo}
          disabled={mutation.isPending}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
